// Offline draft queue for mobile timekeeping.
// Persists the latest desired entries per week to localStorage while offline (or
// when a save fails) and replays them when connectivity returns. The server save
// is a full-replace keyed on the timesheet, so last-write-wins is safe and the
// (timesheet_id, entry_date, task_id) upsert makes replays idempotent.
import { getTenantSlug, type TimesheetEntryInput } from "@/lib/api";

export interface QueuedSave {
  weekISO: string;
  timesheetId: number | null;
  entries: TimesheetEntryInput[];
  updatedAt: number;
}

type QueueMap = Record<string, QueuedSave>;

const key = () => `besthr_tk_offline_${getTenantSlug()}`;

function readQueue(): QueueMap {
  try {
    return JSON.parse(localStorage.getItem(key()) || "{}") as QueueMap;
  } catch {
    return {};
  }
}
function writeQueue(q: QueueMap) {
  try { localStorage.setItem(key(), JSON.stringify(q)); } catch { /* quota / private mode */ }
}

export function queueSave(weekISO: string, timesheetId: number | null, entries: TimesheetEntryInput[]) {
  const q = readQueue();
  q[weekISO] = { weekISO, timesheetId, entries, updatedAt: Date.now() };
  writeQueue(q);
}

export function getQueued(weekISO: string): QueuedSave | null {
  return readQueue()[weekISO] ?? null;
}

export function removeQueued(weekISO: string) {
  const q = readQueue();
  delete q[weekISO];
  writeQueue(q);
}

export function hasPending(): boolean {
  return Object.keys(readQueue()).length > 0;
}

/**
 * Replay every queued save. `flusher` performs the network write for one week;
 * a week is dropped from the queue only when its flush resolves.
 */
export async function flushQueued(
  flusher: (weekISO: string, timesheetId: number | null, entries: TimesheetEntryInput[]) => Promise<unknown>,
): Promise<number> {
  const q = readQueue();
  let flushed = 0;
  for (const item of Object.values(q)) {
    try {
      await flusher(item.weekISO, item.timesheetId, item.entries);
      removeQueued(item.weekISO);
      flushed++;
    } catch {
      // Leave in queue; will retry on the next online event.
    }
  }
  return flushed;
}
